'use client';

import * as Checkbox from '@radix-ui/react-checkbox';
import * as Tabs from '@radix-ui/react-tabs';
import { DynamicIcon } from 'phosphor-icons/react';
import { useState } from 'react';
import { useBreakpoints } from '../../../lib/hooks/useBreakpoints';
import { c } from '../../../utils/tailwind-merge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { IconButton } from '../../atoms/Button/IconButton';
import { AccountTypeTabs } from '../../molecules/AccountTypeTabs/AccountTypeTabs';
import { Banner } from '../../molecules/Banner';
import { FormModal } from '../../molecules/FormModal/FormModal';
import { CheckboxIndicator } from './CheckboxIndicator';
import type { ListData, SaveToListModalProps } from './SaveToListModal.types';

type ListItemProps = {
  list: ListData;
  isSelected: boolean;
  onToggle: (listId: string) => void;
  microcopy: SaveToListModalProps['microcopy'];
};

const ListItem = ({ list, isSelected, onToggle, microcopy }: ListItemProps) => {
  const visibilityIcon =
    list.type === 'private' ? 'Lock' : 'GlobeHemisphereWest';
  const visibilityLabel =
    list.type === 'private' ? microcopy.private : microcopy.public;

  const assetsLabel =
    list.count === 1 ? microcopy.assets_singular : microcopy.assets;
  const ariaLabel = `${list.name}, ${list.count} ${assetsLabel}, ${visibilityLabel}`;

  return (
    <Checkbox.Root
      checked={isSelected}
      onCheckedChange={() => onToggle(list.id)}
      aria-label={ariaLabel}
      className={c(
        'flex w-full cursor-pointer items-center gap-1 rounded-lg border p-2',
        'transition-colors focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900',
        isSelected
          ? 'border-2 border-blue bg-grey-800/30'
          : 'border border-grey-700 hover:border-grey-500'
      )}
    >
      <CheckboxIndicator isSelected={isSelected} />
      <div className="-space-y-[2px] text-left">
        <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium leading-6 tracking-tight text-grey-100">
          {list.name}
        </span>
        <div className="flex items-center gap-2 text-xs text-grey-400">
          <span>
            {list.count} {assetsLabel}
          </span>
          <span>•</span>
          <div className="flex items-center gap-1">
            <DynamicIcon
              iconName={visibilityIcon}
              size={12}
              aria-hidden="true"
              focusable="false"
              className="text-grey-400"
            />
            <span>{visibilityLabel}</span>
          </div>
        </div>
      </div>
    </Checkbox.Root>
  );
};

type ListContentProps = {
  lists: ListData[];
  selectedListIds: Set<string>;
  onToggle: (listId: string) => void;
  microcopy: SaveToListModalProps['microcopy'];
};

const ListContent = ({
  lists,
  selectedListIds,
  onToggle,
  microcopy,
}: ListContentProps) => {
  return (
    <div className="flex flex-col gap-3">
      {lists.map((listItem) => (
        <ListItem
          key={listItem.id}
          list={listItem}
          isSelected={selectedListIds.has(listItem.id)}
          onToggle={onToggle}
          microcopy={microcopy}
        />
      ))}
    </div>
  );
};

export const SaveToListModal = ({
  list,
  multiTabs,
  initialSelectedListIds = [],
  forceEnableSave = false,
  userName,
  userProfilePicture,
  publisherName,
  microcopy,
  onNewList,
  onSave,
  onDismissError,
  isSubmitting = false,
  trigger,
  open: controlledOpen,
  onOpenChange: onOpenChangeProp,
  position,
  showBackdrop,
  serverError,
  className = '',
}: SaveToListModalProps) => {
  const breakpoints = useBreakpoints();
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(
    new Set(initialSelectedListIds)
  );
  const [currentTab, setCurrentTab] = useState<'personal' | 'organization'>(
    'personal'
  );

  const toggleListSelection = (listId: string) => {
    setSelectedListIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    const selectedLists = list.filter((item) => selectedListIds.has(item.id));
    await onSave(selectedLists);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onDismissError?.();
    }
    onOpenChangeProp?.(isOpen);
  };

  const hasSelectionChanged = () => {
    const initialSet = new Set(initialSelectedListIds);

    if (selectedListIds.size !== initialSet.size) {
      return true;
    }

    for (const id of Array.from(selectedListIds)) {
      if (!initialSet.has(id)) {
        return true;
      }
    }

    return false;
  };

  const personalLists = list.filter((item) => item.personal === true);
  const organizationLists = list.filter((item) => item.personal === false);

  const isControlled = controlledOpen !== undefined;
  const triggerElement =
    trigger ??
    (isControlled ? undefined : (
      <IconButton
        className="h-12 w-12 overflow-hidden rounded-lg bg-grey-700 hover:bg-grey-800"
        iconName="BookmarkSimple"
        ariaLabel={microcopy.add}
        iconWeight="bold"
        iconSize={24}
        iconClassName="fill-white hover:fill-white"
      />
    ));

  const isSaveDisabled =
    isSubmitting || (forceEnableSave ? false : !hasSelectionChanged());

  const scrollStyles =
    '[scrollbar-width:thin] [scrollbar-color:theme(colors.grey.700)_theme(colors.grey.900)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-grey-900 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-grey-700 hover:[&::-webkit-scrollbar-thumb]:bg-grey-600';

  const modalContent = (
    <div className="flex flex-1 flex-col pt-4">
      {serverError && microcopy.serverMessage && (
        <div className="mb-5">
          <Banner
            type="errorBlackBg"
            title={microcopy.serverMessage.title}
            description={microcopy.serverMessage.description}
            dismiss={microcopy.serverMessage.dismiss}
            onDismiss={onDismissError ?? (() => {})}
            style="fill"
          />
        </div>
      )}
      <div className="flex flex-col">
        {multiTabs ? (
          <Tabs.Root
            value={currentTab}
            onValueChange={(value) =>
              setCurrentTab(value as 'personal' | 'organization')
            }
            className="flex flex-col"
          >
            <div className="shrink-0">
              <AccountTypeTabs
                userName={userName || ''}
                userProfilePicture={userProfilePicture}
                publisherName={publisherName || ''}
                value={currentTab}
                microcopy={{
                  ariaLabel: microcopy.tabsAriaLabel,
                  personal: microcopy.personal,
                  organization: microcopy.organization,
                }}
                onValueChange={(value) => setCurrentTab(value)}
              />
            </div>

            <div
              className={c(
                'grid max-h-52 overflow-y-auto px-1 pb-1',
                scrollStyles
              )}
            >
              <Tabs.Content
                value="personal"
                forceMount
                className={c(
                  'col-start-1 row-start-1 mt-3',
                  currentTab !== 'personal' && 'hidden'
                )}
                {...(currentTab !== 'personal' ? { inert: '' } : {})}
              >
                <ListContent
                  lists={personalLists}
                  selectedListIds={selectedListIds}
                  onToggle={toggleListSelection}
                  microcopy={microcopy}
                />
              </Tabs.Content>

              <Tabs.Content
                value="organization"
                forceMount
                className={c(
                  'col-start-1 row-start-1 mt-3',
                  currentTab !== 'organization' && 'hidden'
                )}
                {...(currentTab !== 'organization' ? { inert: '' } : {})}
              >
                <ListContent
                  lists={organizationLists}
                  selectedListIds={selectedListIds}
                  onToggle={toggleListSelection}
                  microcopy={microcopy}
                />
              </Tabs.Content>
            </div>
          </Tabs.Root>
        ) : (
          <div
            className={c(
              'mt-3 max-h-52 overflow-y-auto px-1 pb-1',
              scrollStyles
            )}
          >
            <ListContent
              lists={list}
              selectedListIds={selectedListIds}
              onToggle={toggleListSelection}
              microcopy={microcopy}
            />
          </div>
        )}

        <footer className="sticky bottom-0 -mx-5 mt-1 shrink-0 border-t border-grey-800 bg-grey-900 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:justify-between">
            <ButtonV2
              type="button"
              label={microcopy.new}
              variant="secondary"
              size="medium"
              shape="square"
              iconLeft="Plus"
              disabled={isSubmitting}
              onClick={onNewList}
              className="order-2 w-full uppercase md:order-1 md:flex-1 md:flex-shrink-0"
              iconClassName="!mr-2"
            />

            <ButtonV2
              type="button"
              label={microcopy.save}
              variant="primary"
              size="medium"
              shape="square"
              disabled={isSaveDisabled}
              onClick={handleSave}
              iconLeft={isSubmitting ? 'SpinnerGap' : undefined}
              iconWeight={isSubmitting ? 'bold' : undefined}
              iconClassName={isSubmitting ? 'animate-spin' : undefined}
              iconSize={isSubmitting ? 20 : undefined}
              className="order-1 w-full uppercase disabled:border-grey-800 disabled:bg-grey-800 disabled:text-grey-600 md:order-2 md:flex-1 md:flex-shrink-0"
            />
          </div>
        </footer>
      </div>
    </div>
  );

  return (
    <FormModal
      dark
      size={breakpoints?.isMobile ? 'medium' : 'xsmall'}
      open={controlledOpen}
      trigger={triggerElement}
      title={microcopy.title}
      subtitle={microcopy.subTitle}
      close={microcopy.close}
      onOpenChange={handleOpenChange}
      content={modalContent}
      position={position}
      showBackdrop={showBackdrop}
      className={c('h-auto md:max-w-[420px]', className)}
    />
  );
};

SaveToListModal.displayName = 'SaveToListModal';
